/**
 * Pure copy for the protection surface's own threat-context panel:
 *
 *  - bands and labels are served VERBATIM and derived server-side from ONE
 *    shared constant; nothing in this file (or anywhere in web/) derives a
 *    label from a number;
 *  - deltas are shown SIGNED — the sign is the information. `signedDelta` is
 *    reused by the Likeness Health Score's events feed
 *    (screens/quiz.tsx#QuizScreen) as well as the retired protection-score
 *    history panel it was written for — the movement-with-a-sign rule is the
 *    same rule on both surfaces;
 *  - severity drives urgency copy, ncii_suspected ≫ benign_copy, and severity
 *    only ever arrives on a CONFIRMED finding (the api withholds it otherwise);
 *  - anything the server did not map (an unknown cause, kind or severity)
 *    renders as nothing or a neutral line, never as a raw enum value dressed
 *    up as a sentence.
 *
 * Recommendation cards no longer need copy derived here at all — the
 * business catalog (src/recommendations/catalog.ts) serves `title` and
 * `description` VERBATIM, same rule as the score band. The target→route
 * mapping that surface needs (`recommendationHref`) used to live here too,
 * but moved to flow/recommendations.ts once screens/dashboard.tsx became a
 * second caller alongside screens/protection.tsx.
 */

/** "+5" / "-3" / "0" — the movement, with its sign. */
export function signedDelta(delta: number): string {
  return delta > 0 ? `+${String(delta)}` : String(delta);
}

/**
 * Urgency copy per severity, for CONFIRMED findings only. An unknown severity
 * (services may add one additively) renders nothing rather than an invented
 * sentence — and none of these may ever contain the word "safe".
 */
const SEVERITY_COPY: Record<string, string> = {
  ncii_suspected: 'Suspected intimate-image abuse. Treat this as urgent.',
  explicit_unmatched: 'Explicit content; the face match could not be verified.',
  unassessed: 'Confirmed, severity not yet assessed.',
  benign_copy: 'A repost of your photo, without explicit content.',
  likely_not_subject: 'Review suggests this may not be you.',
};

export function severityCopy(severity: string | null): string | null {
  if (severity === null) return null;
  return SEVERITY_COPY[severity] ?? null;
}
